import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Browser speech-to-text using the Web Speech API.
 * Works best on Chromium browsers.
 */
export function useSpeechToText({
  lang = 'en-US',
  interimResults = true,
  continuous = false,
  onFinalTranscript,
  onInterimTranscript,
  onError,
  onListeningChange,
} = {}) {
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [lastError, setLastError] = useState(null);

  const isSupported = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      setIsListening(false);
      return;
    }
    try {
      recognition.stop();
    } catch {
      // ignore
    }
  }, []);

  const start = useCallback(() => {
    if (!isSupported) {
      const err = new Error('Speech recognition is not supported in this browser.');
      setLastError(err);
      if (typeof onError === 'function') onError(err);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = lang;
    recognition.interimResults = Boolean(interimResults);
    recognition.continuous = Boolean(continuous);

    recognition.onstart = () => {
      setIsListening(true);
      setLastError(null);
      if (typeof onListeningChange === 'function') onListeningChange(true);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (typeof onListeningChange === 'function') onListeningChange(false);
    };

    recognition.onerror = (event) => {
      const err = new Error(event?.error || 'Speech recognition error');
      err.name = 'SpeechRecognitionError';
      setLastError(err);
      if (typeof onError === 'function') onError(err);
      // Most errors end the session.
      setIsListening(false);
      if (typeof onListeningChange === 'function') onListeningChange(false);
    };

    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const text = (res?.[0]?.transcript || '').trim();
        if (!text) continue;
        if (res.isFinal) finalText += (finalText ? ' ' : '') + text;
        else interim += (interim ? ' ' : '') + text;
      }

      if (interim && typeof onInterimTranscript === 'function') {
        onInterimTranscript(interim);
      }
      if (finalText && typeof onFinalTranscript === 'function') {
        onFinalTranscript(finalText);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      // Common if start is called twice quickly
      const err = e instanceof Error ? e : new Error('Failed to start speech recognition');
      setLastError(err);
      if (typeof onError === 'function') onError(err);
      setIsListening(false);
      if (typeof onListeningChange === 'function') onListeningChange(false);
    }
  }, [continuous, interimResults, isSupported, lang, onError, onFinalTranscript, onInterimTranscript, onListeningChange]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  // Cleanup on unmount
  useEffect(() => stop, [stop]);

  return {
    isSupported,
    isListening,
    lastError,
    start,
    stop,
    toggle,
  };
}
